import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useLive } from '@/lib/use-live';
import { useContainer, useSessionState } from '@/features/session/use-session';

/** De quanto em quanto tempo, com sinal e fila, a fila tenta subir de novo. */
const TENTAR_A_CADA_MS = 20_000;

/**
 * A fila de quem está com a sessão (RN-610): o que ainda vai subir sozinho, e o que o servidor
 * recusou e espera a pessoa decidir. Zero enquanto não se sabe.
 */
export function useQueueCounts(): { readonly pending: number; readonly rejected: number } {
  const container = useContainer();
  const { session } = useSessionState();
  const fila = useLive(
    () => (session === undefined ? Promise.resolve([]) : container.outbox.list(session.userId)),
    [session?.userId],
  );
  const rejected = (fila ?? []).filter((entrada) => entrada.state === 'rejected').length;
  return { pending: (fila?.length ?? 0) - rejected, rejected };
}

/**
 * Sobe a fila da Conferência sozinha (Fase 43, RN-605).
 *
 * Ao entrar, quando o sinal volta, e a cada 20 s enquanto houver o que subir. Sem sinal não
 * tenta: quem descobre a volta é a leitura das telas (`retryWhileOffline`), e a volta chama
 * esta. A fila sobe com a sessão de quem está aqui, e só os registros dela (RN-610).
 */
export function useOutboxSync(): void {
  const container = useContainer();
  const { session } = useSessionState();
  const { connection } = container;
  const online = useSyncExternalStore(connection.subscribe, () => connection.snapshot().online);
  // Só o que vai subir sozinho: o recusado espera a pessoa, e não um relógio.
  const pendentes = useQueueCounts().pending;

  const sessao = useRef(session);
  sessao.current = session;

  useEffect(() => {
    const atual = sessao.current;
    if (atual === undefined || !online || pendentes === 0) return undefined;
    void container.outbox.flush(atual);
    const relogio = setInterval(() => {
      if (sessao.current !== undefined) void container.outbox.flush(sessao.current);
    }, TENTAR_A_CADA_MS);
    return () => clearInterval(relogio);
  }, [container, session?.userId, online, pendentes]);
}
